import React from 'react';
import { getRecipeImageUrl, getTotalTimeMinutes, formatMinutes } from '../../utils/recipeUtils';
import { useLanguage } from '../../i18n/LanguageContext';

const RecipeDetailHero = ({
  recipe,
  currentUser = null,
  isFavorited = false,
  isDeleting = false,
  onFavoriteToggle = null,
  onEdit = null,
  onDelete = null,
  onShare = null,
  usePlaceholderImage = true,
}) => {
  const { lang, t } = useLanguage();
  const rd = t.recipeDetail;
  const favLabels = {
    add: t.favorites?.favoriteAdd ?? 'Save to favorites',
    remove: t.favorites?.favoriteRemove ?? 'Remove from favorites',
  };

  if (!recipe) {
    return null;
  }

  const recipeId = recipe._id || recipe.id;
  const canManage =
    currentUser?.role === 'ADMIN' || (currentUser && currentUser.id === recipe.recipeUserId);

  const totalTime = getTotalTimeMinutes(recipe);
  const hasImageUrl = Boolean(String(recipe?.imageUrl || '').trim());
  const imageSrc = hasImageUrl
    ? getRecipeImageUrl(recipe)
    : (usePlaceholderImage ? getRecipeImageUrl(recipe) : null);
  const title = recipe.title || 'Untitled recipe';
  const description = recipe.description || '';

  return (
    <section className={`recipe-detail-hero${imageSrc ? '' : ' no-media'}`}>
      {imageSrc ? (
        <div className="recipe-detail-hero-media">
          <img src={imageSrc} alt={title} loading="lazy" />
          {recipe.tags?.length ? <span className="hero-tag">{recipe.tags[0]}</span> : null}
          <div className="detail-hero-top-actions">
            {onFavoriteToggle ? (
              <button
                type="button"
                className={`recipe-fav-button${isFavorited ? ' is-favorited' : ''}`}
                aria-label={isFavorited ? favLabels.remove : favLabels.add}
                onClick={() => onFavoriteToggle(recipe)}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  {isFavorited ? 'favorite' : 'favorite_border'}
                </span>
              </button>
            ) : null}
            {canManage && onEdit ? (
              <button
                type="button"
                className="recipe-action-button"
                aria-label={`Edit recipe ${title}`}
                onClick={() => onEdit(recipe)}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  edit
                </span>
              </button>
            ) : null}
            {canManage && onDelete ? (
              <button
                type="button"
                className="recipe-action-button danger"
                aria-label={`Delete recipe ${title}`}
                disabled={isDeleting}
                onClick={() => onDelete(recipe)}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  {isDeleting ? 'hourglass_top' : 'delete'}
                </span>
              </button>
            ) : null}
          </div>
          {onShare ? (
            <button
              type="button"
              className="recipe-share-overlay-btn"
              aria-label="Share recipe"
              onClick={() => onShare(recipe)}
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                share
              </span>
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="recipe-detail-hero-copy">
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
        <div className="hero-meta">
          {recipe.recipeUserName ? <span>{rd.by} {recipe.recipeUserName}</span> : null}
          {totalTime ? <span>{formatMinutes(totalTime, lang)}</span> : null}
          {recipe.servings ? <span>{recipe.servings} {rd.servingsLabel}</span> : null}
        </div>
      </div>
    </section>
  );
};

export default RecipeDetailHero;
